<template>
  <ValidationProvider :rules="rules" :vid="prefixLabel + ' ' + label" immediate v-slot="{ failed }">
    <md-autocomplete
      ref="md-autocomplete"
      v-model="internal_value"
      :md-options="md_options"
      autocomplete="url"
      :disabled="disabled"
      :class="{ disabled, 'md-error': failed}"
      :readonly="readonly"
      @md-selected="onItemSelected"
      :md-open-on-focus="focus"
    >
      <label>
        {{ label }}
        <span v-if="required">&nbsp;*</span>
      </label>

      <template slot="md-autocomplete-item" slot-scope="{ item, term }">
        <span class="md-autocomplete-item" 
        :class="{ first: enable_filter && term && filtered_options.length && item == filtered_options[0], 'md-selected': item == internal_value ? true : false, 'inactive-broker': isInactiveUser[item] }">{{ (displaylabel)?item[displaylabel]:item }}
        </span>
        <!-- The following block highlights the matching start of the option but is not compatible with the more complex matching done now -->
        <!-- <div
          class="md-autocomplete-item"
          :class="{ first: term && filtered_options.length && item == filtered_options[0] }"
        >
          <span class="highlight">{{ item.substring(0, term.length) }}</span
          ><span>{{ item.substring(term.length) }}</span>
        </div> -->
      </template>
    </md-autocomplete>
    <slot name="extraFields">
    </slot>
    <span v-if="failed" class="error-msg">{{ errormsg }}</span>
  </ValidationProvider>
</template>

<script>
import { SlideYDownTransition } from "vue2-transitions";

export default {
  components: {
    SlideYDownTransition,
  },
  data() {
    return {
      internal_value: "",
      md_autocomplete_component: null,
      enable_filter: true,
    };
  },
  props: {
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    limitedDropdownHeight: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    value: {
      type: [String, Number, Object, Array],
      default: "",
    },
    options: [String, Array, Object],
    force: {
      type: [Boolean, String],
      default: false,
    },
    prefix: {
      type: String,
      default: "",
    },
    suffix: {
      type: String,
      default: "",
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    errormsg: {
      type: String,
      default: "Please enter the correct information.",
    },
    focus:{
      type: Boolean,
      default: true,
    },
    prefixLabel: {
      type: String,
      default: "",
    },
    returntype:{
      type: String,
      default: 'string',
    },
    displaylabel:{
      type: String,
      default: null,
    },
    autocompleteDisabled: {
      type: Boolean,
      default: false
    },

    alphaSpaces: {
      type: Boolean,
      default: false
    },
    isInactiveUser: {
       type: Object,
       default: () => ({})
    }
  },
  watch: {
    value: {
      handler(value) {
        this.internal_value = value ? (this.returntype== 'string')? String(value) : value : '';
      },
      immediate: true,
    },
    internal_value(value) {
      // re-enable options filtering
      this.enable_filter = true;

      // if force, only update external_value if the internal_value is empty or one of the options
      if (this.force) {
        if (!value || this.option_entries.includes(value)) {
          this.external_value = value;
        }
      } else {
        this.external_value = value;
      }
    },
    external_value(value) {
      this.internal_value = value;
    },
  },
  computed: {
    rules() {
      let rules = [];
      if(this.required) rules.push('required');
      if(this.alphaSpaces) rules.push(`alpha_spaces:${this.alphaSpaces}`);
      return rules.join("|");
    },
    // normalise the different formats options can be given in
    option_entries() {
      switch (typeof this.options) {
        case "string":
          return this.options.split(",").map(v => (!this.displaylabel) ? String(v) : v);
        case "array":
          return this.options.map(v => (!this.displaylabel) ? String(v) : v);
        case "object":
          return this.options ? Object.values(this.options).map(v => (!this.displaylabel) ? String(v) : v) : [];
        default:
          return [];
      }
    },
    // filter options based on internal_value
    filtered_options() {
      if (this.internal_value && this.enable_filter) {
        let search_term = String(this.internal_value).toLowerCase().trim();
        let search_terms = search_term.split(' ');
        let values_and_matches = this.option_entries.map(value => ({value, match: this.getMatchQuality(search_terms, (this.displaylabel) ? String(value[this.displaylabel]).toLowerCase() : String(value).toLowerCase())}));
        let filtered = values_and_matches.filter(({match}) => match > 0);
        let sorted = filtered.sort(({match:a}, {match:b}) => b-a);
        let values = sorted.map(({value}) => value);

        return values.slice(0,100);
      } else {
        return this.option_entries.slice(0,100);
      }
    },
    md_options() {
      // returning a Promise instructs md-autocomplete that we have done the filtering on our side
      return new Promise((resolve) => resolve(this.filtered_options));
    },
    external_value: {
      get() {
        // if falsy, return empty string
        return !this.value ? "" : String(this.value);
      },
      set(value) {
        this.$emit("input", value);
      },
    },
  },
  methods: {
    finaliseValue(align_value) {
      let final_value = this.internal_value;

      if (align_value === "first-option") {
        // find the first matching option, or null if internal value is empty
        final_value = "";
        if (this.internal_value) {
          final_value = (this.filtered_options && this.filtered_options[0]) || "";
        }
      } else if (align_value === "") {
        if(!(this.filtered_options && this.filtered_options.includes(final_value))) {
          final_value = "";
        }
      }

      // synchronise internal and external value
      this.internal_value = (this.displaylabel) ? final_value[this.displaylabel] : final_value;
      this.external_value = (this.displaylabel) ? final_value[this.displaylabel] : final_value;
      this.$emit('select', final_value)
    },
    onBlur(trigger, options_shown = false) {
      let align_value = false;
      if ((options_shown && this.filtered_options.length) && trigger == "key.tab" && this.enable_filter && !this.autocompleteDisabled) {
        align_value = "first-option";
      } else {
        align_value = this.force ? "" : false;
      }

      this.finaliseValue(align_value);
      this.$emit("blur", trigger);
    },
    onInputBlur(event) {
      // if the focus loss event is caused by a click on md-autocomplete-items,
      // we need to let the component handle the click event, update the value and emit the events
      let md_autocomplete_items =
        event.relatedTarget && event.relatedTarget.closest(".md-autocomplete-items");

      if (this.md_autocomplete_component && !md_autocomplete_items) {
        this.onBlur("key.tab", this.md_autocomplete_component.showMenu);
        // wait some time before closing the pop items to prevent UI conflicts
        setTimeout(() => {
          this.md_autocomplete_component.hideOptions();
          this.md_autocomplete_component.showMenu = false;
        }, 100);
      }
    },
    onInputFocus(event) {
      this.enable_filter = false;
      let input_element = event.target;
      input_element.select();
      this.md_autocomplete_component.showMenu = true;
      this.md_autocomplete_component.showOptions();
      this.$nextTick(() => {
      const menuContent = document.querySelector('.md-menu-content');
        if (menuContent) {
          if (this.limitedDropdownHeight) {
            menuContent.classList.add('limited-dropdown-height');
          } else {
            menuContent.classList.remove('limited-dropdown-height');
          }
        }
      });
    },
    onItemSelected(event) {
      this.internal_value = event;
      this.onBlur("click.item", true);
      this.$emit("item-selected", event);
    },
    onKeyUp(event) {
      switch(event.key) {
        case 'Enter':
          this.md_autocomplete_component.hideOptions();
          this.md_autocomplete_component.showMenu = false;
          this.$nextTick(() => {
            this.md_autocomplete_component.showMenu = true;
            this.md_autocomplete_component.showOptions();
          });
          break;
      }
    },
    getMatchQuality(search_terms, value) {
      let includes_all = search_terms.every(search_term => value.includes(search_term))
      let match_size = search_terms.reduce((a,s) => a+s.length, 0);
      let start_with = search_terms.some(search_term => value.startsWith(search_term))
      let quality = includes_all * (match_size / value.length + start_with);
      return quality
    },
  },
  mounted() {
    // add blur and keyup event
    this.md_autocomplete_component = this.$refs["md-autocomplete"];
    let input_element = this.md_autocomplete_component.$el.querySelector("input");
    input_element.addEventListener("focus", this.onInputFocus);
    input_element.addEventListener("blur", this.onInputBlur);
    input_element.addEventListener("keyup", this.onKeyUp);
  },
  beforeUnmount() {
    // remove blur and keyup event
    let md_autocomplete_component = this.md_autocomplete_component || this.$refs["md-autocomplete"];
    let input_element = md_autocomplete_component.$el.querySelector("input");
    input_element.removeEventListener("focus", this.onInputFocus);
    input_element.removeEventListener("blur", this.onInputBlur);
    input_element.removeEventListener("keyup", this.onKeyUp);
    this.md_autocomplete_component = null;
  },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/theme.scss";

.disabled {
  opacity: 0.7;
}

.inactive-broker {
  color: gray !important;
  font-style: italic;
}

.md-autocomplete-item {
  white-space: pre-wrap;
  position: unset;
  & > * {
    padding-left: 0px !important;
    padding-right: 0px !important;
  }

  &.first {
    border-radius: 3px;
    border: 1px dashed rgba(0,0,0,0.3);
  }
  & .highlight {
    text-decoration: underline;
  }
}
</style>
<style lang="scss">
.limited-dropdown-height {
  max-height: 20vh !important;
  overflow-y: auto !important;
}
@import "../../assets/scss/theme.scss";
.md-list-item-content:has(span.md-selected) {
  background-color: $ausloans-blue;
  color: $ausloans-grey;
}
</style>
